import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtIDR } from "@/lib/format";
import { toast } from "sonner";
import {
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";
import { Scale, BookOpen, TrendingUp, PieChart as PieIcon, Plus, Trash2, CheckCircle2, AlertCircle } from "lucide-react";

const TYPE_LABEL = { asset: "Aset", liability: "Kewajiban", equity: "Ekuitas", revenue: "Pendapatan", expense: "Beban" };
const TYPE_ORDER = ["asset", "liability", "equity", "revenue", "expense"];
const COLORS = ["#0f172a", "#6366f1", "#ec4899", "#10b981", "#f59e0b", "#06b6d4", "#ef4444", "#8b5cf6", "#84cc16"];

const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export default function Accounting() {
  const { branchId } = useBranch();
  const [tab, setTab] = useState("income");
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [asOf, setAsOf] = useState(today());

  const [income, setIncome] = useState(null);
  const [balance, setBalance] = useState(null);
  const [sales, setSales] = useState(null);
  const [coa, setCoa] = useState([]);

  const [coaDialogOpen, setCoaDialogOpen] = useState(false);
  const [coaForm, setCoaForm] = useState({ code: "", name: "", type: "asset", category: "" });

  const params = useMemo(() => ({ branch_id: branchId, date_from: dateFrom, date_to: dateTo }), [branchId, dateFrom, dateTo]);

  const loadIncome = () => api.get("/accounting/income-statement", { params }).then((r) => setIncome(r.data));
  const loadSales = () => api.get("/accounting/sales-breakdown", { params }).then((r) => setSales(r.data));
  const loadBalance = () => api.get("/accounting/balance-sheet", { params: { branch_id: branchId, as_of: asOf } }).then((r) => setBalance(r.data));
  const loadCoa = () => api.get("/accounting/coa").then((r) => setCoa(r.data));

  useEffect(() => { loadIncome(); loadSales(); /* eslint-disable-next-line */ }, [branchId, dateFrom, dateTo]);
  useEffect(() => { loadBalance(); /* eslint-disable-next-line */ }, [branchId, asOf]);
  useEffect(() => { loadCoa(); }, []);

  const submitCoa = async () => {
    if (!coaForm.code || !coaForm.name) { toast.error("Kode & nama wajib"); return; }
    try {
      await api.post("/accounting/coa", coaForm);
      toast.success("Akun ditambahkan");
      setCoaDialogOpen(false);
      setCoaForm({ code: "", name: "", type: "asset", category: "" });
      loadCoa();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const deleteCoa = async (a) => {
    if (!window.confirm(`Hapus akun ${a.code} ${a.name}?`)) return;
    try { await api.delete(`/accounting/coa/${a.id}`); toast.success("Akun dihapus"); loadCoa(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const pieData = sales ? [
    { name: "Treatment", value: sales.treatment_revenue },
    { name: "Produk", value: sales.product_revenue },
  ] : [];

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Akuntansi</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Laba Rugi, Neraca & analisa penjualan — otomatis dari transaksi, inventory & beban.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">Dari</Label>
            <Input type="date" data-testid="acc-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" />
          </div>
          <div>
            <Label className="text-xs">Sampai</Label>
            <Input type="date" data-testid="acc-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" />
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="accounting-tabs">
          <TabsTrigger value="income" data-testid="tab-income"><TrendingUp className="mr-1.5 h-3.5 w-3.5" /> Laba Rugi</TabsTrigger>
          <TabsTrigger value="balance" data-testid="tab-balance"><Scale className="mr-1.5 h-3.5 w-3.5" /> Neraca</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales"><PieIcon className="mr-1.5 h-3.5 w-3.5" /> Produk vs Treatment</TabsTrigger>
          <TabsTrigger value="coa" data-testid="tab-coa"><BookOpen className="mr-1.5 h-3.5 w-3.5" /> Chart of Accounts</TabsTrigger>
        </TabsList>

        {/* ---- Income Statement ---- */}
        <TabsContent value="income" className="mt-4">
          {income && (
            <Card data-testid="income-statement">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Laporan Laba Rugi</CardTitle>
                <CardDescription>{income.date_from} s/d {income.date_to} • {income.tx_count} transaksi</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 max-w-2xl">
                <SectionTitle>Pendapatan</SectionTitle>
                {income.revenue.lines.map((l) => <Line key={l.code} code={l.code} name={l.name} amount={l.amount} />)}
                <Line name="Total Pendapatan" amount={income.revenue.total} bold testid="income-revenue-total" />
                <SectionTitle>Harga Pokok Penjualan</SectionTitle>
                <Line code={income.cogs.code} name={income.cogs.name} amount={-income.cogs.amount} />
                <Line name="Laba Kotor" amount={income.gross_profit} bold sub={`Margin ${income.gross_margin_pct}%`} testid="income-gross" />
                <SectionTitle>Beban Operasional</SectionTitle>
                {income.expenses.lines.filter((l) => l.amount > 0).map((l) => <Line key={l.code} code={l.code} name={l.name} amount={-l.amount} />)}
                <Line name="Total Beban" amount={-income.expenses.total} bold />
                <div className="mt-3 flex items-center justify-between rounded-lg bg-foreground px-4 py-3 text-background">
                  <span className="font-heading">Laba Bersih</span>
                  <span className="font-heading text-xl" data-testid="income-net">{fmtIDR(income.net_profit)} <span className="text-xs opacity-70">({income.net_margin_pct}%)</span></span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---- Balance Sheet ---- */}
        <TabsContent value="balance" className="mt-4">
          <div className="mb-3 flex items-end gap-2">
            <div>
              <Label className="text-xs">Per Tanggal</Label>
              <Input type="date" data-testid="acc-as-of" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-[160px]" />
            </div>
            {balance && (
              <Badge data-testid="balance-status" className={balance.balanced ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>
                {balance.balanced ? <><CheckCircle2 className="mr-1 h-3 w-3" /> Seimbang</> : <><AlertCircle className="mr-1 h-3 w-3" /> Tidak Seimbang</>}
              </Badge>
            )}
          </div>
          {balance && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="balance-sheet">
              <Card>
                <CardHeader><CardTitle className="font-heading text-lg">Aset</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {balance.assets.lines.map((l) => <Line key={l.code} code={l.code} name={l.name} amount={l.amount} />)}
                  <Line name="Total Aset" amount={balance.assets.total} bold testid="balance-assets-total" />
                </CardContent>
              </Card>
              <div className="space-y-4">
                <Card>
                  <CardHeader><CardTitle className="font-heading text-lg">Kewajiban</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {balance.liabilities.lines.map((l) => <Line key={l.code} code={l.code} name={l.name} amount={l.amount} />)}
                    <Line name="Total Kewajiban" amount={balance.liabilities.total} bold />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading text-lg">Ekuitas</CardTitle></CardHeader>
                  <CardContent className="space-y-1">
                    {balance.equity.lines.map((l) => <Line key={l.code} code={l.code} name={l.name} amount={l.amount} />)}
                    <Line name="Total Ekuitas" amount={balance.equity.total} bold />
                    <Line name="Kewajiban + Ekuitas" amount={balance.liabilities.total + balance.equity.total} bold testid="balance-liab-equity-total" />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---- Sales breakdown ---- */}
        <TabsContent value="sales" className="mt-4">
          {sales && (
            <div className="space-y-4" data-testid="sales-breakdown">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Total Penjualan" value={fmtIDR(sales.total_revenue)} />
                <Stat label="Treatment" value={fmtIDR(sales.treatment_revenue)} sub={`${sales.treatment_pct}% • ${sales.treatment_tx_count} tx`} testid="sales-treatment" />
                <Stat label="Produk" value={fmtIDR(sales.product_revenue)} sub={`${sales.product_pct}% • ${sales.product_tx_count} tx`} testid="sales-product" />
                <Stat label="Rasio Treatment:Produk" value={`${sales.treatment_pct}:${sales.product_pct}`} />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="font-heading text-lg">Komposisi Penjualan</CardTitle></CardHeader>
                  <CardContent style={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                          {pieData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtIDR(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading text-lg">Tren Harian</CardTitle></CardHeader>
                  <CardContent style={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={sales.trend}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => (d || "").slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                        <Tooltip formatter={(v) => fmtIDR(v)} />
                        <Area type="monotone" dataKey="treatment" stackId="1" stroke="#0f172a" fill="#0f172a" fillOpacity={0.7} />
                        <Area type="monotone" dataKey="product" stackId="1" stroke="#ec4899" fill="#ec4899" fillOpacity={0.7} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="font-heading text-lg">Top Treatment</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow><TableHead>Treatment</TableHead><TableHead className="text-right">Tx</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {sales.top_treatments.map((t, i) => (
                          <TableRow key={i}><TableCell className="text-sm">{t.name}</TableCell><TableCell className="text-right text-sm">{t.count}</TableCell><TableCell className="text-right text-sm font-medium">{fmtIDR(t.revenue)}</TableCell></TableRow>
                        ))}
                        {sales.top_treatments.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-4">Belum ada data</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="font-heading text-lg">Top Produk Retail</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow><TableHead>Produk</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {sales.top_products.map((p, i) => (
                          <TableRow key={i}><TableCell className="text-sm">{p.name}</TableCell><TableCell className="text-right text-sm">{p.qty}</TableCell><TableCell className="text-right text-sm font-medium">{fmtIDR(p.revenue)}</TableCell></TableRow>
                        ))}
                        {sales.top_products.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-4">Belum ada penjualan produk</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---- Chart of Accounts ---- */}
        <TabsContent value="coa" className="mt-4">
          <div className="mb-3 flex justify-end">
            <Button data-testid="add-coa-btn" onClick={() => setCoaDialogOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> Tambah Akun</Button>
          </div>
          <div className="space-y-4" data-testid="coa-list">
            {TYPE_ORDER.map((type) => {
              const rows = coa.filter((a) => a.type === type);
              if (rows.length === 0) return null;
              return (
                <Card key={type}>
                  <CardHeader className="py-3"><CardTitle className="font-heading text-base">{TYPE_LABEL[type]}</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader><TableRow><TableHead className="w-24">Kode</TableHead><TableHead>Nama Akun</TableHead><TableHead>Kategori</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {rows.map((a) => (
                          <TableRow key={a.id} data-testid={`coa-${a.code}`}>
                            <TableCell className="font-mono text-sm">{a.code}</TableCell>
                            <TableCell className="text-sm">{a.name} {a.is_system && <Badge variant="outline" className="ml-1 text-[10px]">sistem</Badge>}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{a.category || "—"}</TableCell>
                            <TableCell className="text-right">
                              {!a.is_system && (
                                <Button size="sm" variant="ghost" data-testid={`del-coa-${a.code}`} onClick={() => deleteCoa(a)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add COA dialog */}
      <Dialog open={coaDialogOpen} onOpenChange={setCoaDialogOpen}>
        <DialogContent data-testid="coa-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Tambah Akun Baru</DialogTitle>
            <DialogDescription>Akun custom di luar akun sistem bawaan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kode *</Label><Input data-testid="coa-code" value={coaForm.code} onChange={(e) => setCoaForm({ ...coaForm, code: e.target.value })} placeholder="6100" /></div>
              <div>
                <Label>Tipe *</Label>
                <Select value={coaForm.type} onValueChange={(v) => setCoaForm({ ...coaForm, type: v })}>
                  <SelectTrigger data-testid="coa-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPE_ORDER.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Nama Akun *</Label><Input data-testid="coa-name" value={coaForm.name} onChange={(e) => setCoaForm({ ...coaForm, name: e.target.value })} placeholder="Beban Penyusutan" /></div>
            <div><Label>Kategori</Label><Input data-testid="coa-category" value={coaForm.category} onChange={(e) => setCoaForm({ ...coaForm, category: e.target.value })} placeholder="opsional" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoaDialogOpen(false)}>Batal</Button>
            <Button data-testid="coa-save-btn" onClick={submitCoa}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <div className="pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</div>
);

const Line = ({ code, name, amount, bold, sub, testid }) => (
  <div className={`flex items-center justify-between py-1.5 ${bold ? "border-t border-border font-medium" : ""}`}>
    <span className="text-sm">{code && <span className="font-mono text-xs text-muted-foreground mr-2">{code}</span>}{name}{sub && <span className="ml-2 text-xs text-muted-foreground">{sub}</span>}</span>
    <span className={`text-sm ${amount < 0 ? "text-rose-600" : ""}`} data-testid={testid}>{fmtIDR(amount)}</span>
  </div>
);

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="mt-1 font-heading text-lg">{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);
